import type { BrowserOptions, Breadcrumb, ErrorEvent, ReplayFrameEvent } from "@sentry/react";

/**
 * Sentry へ送るイベントから URL のクエリ文字列・フラグメントを除去する。
 *
 * Firebase Storage のダウンロード URL（`?alt=media&token=…`）は token だけで
 * 本人確認書類などの画像に恒久的にアクセスできるため、browserTracing の
 * resource span や fetch breadcrumb、Session Replay の custom event 経由で
 * Sentry に送られないようにする。
 * 管理画面の検索条件（顧客名など）がクエリに載るケースも同様に除去する。
 * `/transactions?name=…` や `GET /api?name=…` のようなパスのみの文字列（transaction 名など）も対象とする。
 *
 * 循環参照・過度に深いネストはその項目だけを除去し、イベント自体は送信する。
 * それでも想定外の例外が起きた場合は、未スクラブのまま送信せずイベントを破棄する（hook が null を返す）。
 */

// @sentry/react は TransactionEvent を再エクスポートしていないため、オプション型から導出する
type TransactionEvent = Parameters<NonNullable<BrowserOptions["beforeSendTransaction"]>>[0];

const URL_PATTERN = /(?:https?:)?\/\/[^\s"'<>)]+/gi;
const URL_QUERY_OR_FRAGMENT_PATTERN = /[?#].*$/;
// 先頭（空白・HTTP メソッドを除く）が `/` の文字列はパスとみなす
const PATH_ONLY_PATTERN = /^\s*(?:[A-Z]+\s+)?\//;
// パス中の最初の `?`/`#` から次の空白までを除去する（空白以降の自由テキストは残す）
const PATH_QUERY_OR_FRAGMENT_PATTERN = /[?#]\S*/;
const REMOVED_KEYS: ReadonlySet<string> = new Set(["http.query", "http.fragment", "query_string", "fragment"]);
const MAX_DEPTH = 20;
// rrweb EventType.Custom
const REPLAY_CUSTOM_EVENT_TYPE = 5;
const REPLAY_SCRUBBED_TAGS: ReadonlySet<unknown> = new Set(["performanceSpan", "breadcrumb"]);

/** 循環参照・深すぎるネストなど、結果から取り除く値の目印 */
const DROPPED = Symbol("sentry-scrub-dropped");

const stripPathQuery = (value: string): string => (PATH_ONLY_PATTERN.test(value) ? value.replace(PATH_QUERY_OR_FRAGMENT_PATTERN, "") : value);

/** 絶対 URL（http/https/プロトコル相対）と、パスのみの文字列からクエリ・フラグメントを除去する */
export const stripUrlQuery = (value: string): string => stripPathQuery(value.replace(URL_PATTERN, (url) => url.replace(URL_QUERY_OR_FRAGMENT_PATTERN, "")));

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * `ancestors` は現在の走査経路上にあるオブジェクトの集合。
 * 経路上に再登場した場合のみ循環とみなし、同一オブジェクトの共有参照（循環でないもの）はそのまま走査する。
 */
const scrubValue = (value: unknown, depth: number, ancestors: WeakSet<object>): unknown => {
  if (typeof value === "string") {
    return stripUrlQuery(value);
  }
  if (!isRecord(value) || value instanceof Date) {
    return value;
  }
  if (depth > MAX_DEPTH || ancestors.has(value)) {
    return DROPPED;
  }
  ancestors.add(value);
  const result = Array.isArray(value)
    ? value.map((item: unknown) => {
        const scrubbed = scrubValue(item, depth + 1, ancestors);
        return scrubbed === DROPPED ? undefined : scrubbed;
      })
    : Object.fromEntries(
        Object.entries(value)
          .filter(([key]) => !REMOVED_KEYS.has(key))
          .map(([key, item]) => [key, scrubValue(item, depth + 1, ancestors)] as const)
          .filter(([, item]) => item !== DROPPED),
      );
  ancestors.delete(value);
  return result;
};

const scrubRoot = (value: unknown): unknown => {
  const scrubbed = scrubValue(value, 0, new WeakSet());
  return scrubbed === DROPPED ? undefined : scrubbed;
};

/**
 * 入力を変更せず、全文字列の URL クエリを除去したディープコピーを返す。
 * トップレベルの `sdkProcessingMetadata` は SDK 内部用（送信前に SDK が削除する）で、
 * Scope / Client など巨大かつ循環する参照を含むため走査せず同一参照のまま残す。
 */
export const scrubEvent = <T>(event: T): T => {
  if (!isRecord(event) || Array.isArray(event) || !("sdkProcessingMetadata" in event)) {
    return scrubRoot(event) as T;
  }
  const { sdkProcessingMetadata, ...rest } = event;
  const scrubbed = scrubRoot(rest);
  return { ...(isRecord(scrubbed) ? scrubbed : {}), sdkProcessingMetadata } as T;
};

const safeScrub = <T>(event: T): T | null => {
  // 防御的ガード: 通常の経路では scrubEvent は例外を投げない（循環・深さは項目単位で除去する）。
  // getter の例外など想定外の失敗時のみ、未スクラブで送信するよりイベントを破棄する。
  try {
    return scrubEvent(event);
  } catch {
    return null;
  }
};

export const beforeSend = (event: ErrorEvent): ErrorEvent | null => safeScrub(event);

export const beforeSendTransaction = (event: TransactionEvent): TransactionEvent | null => safeScrub(event);

export const beforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => safeScrub(breadcrumb);

const isScrubTargetReplayEvent = (event: unknown): boolean =>
  isRecord(event) && event.type === REPLAY_CUSTOM_EVENT_TYPE && isRecord(event.data) && REPLAY_SCRUBBED_TAGS.has(event.data.tag);

/**
 * Session Replay の記録イベント用。resource span（Storage URL）や navigation の履歴は
 * rrweb の custom event（type 5）として記録され beforeSend 等を経由しないため、ここで除去する。
 * DOM スナップショット等の他種別は巨大になり得るのでコピーせずそのまま返す。
 */
export const beforeAddRecordingEvent = (event: ReplayFrameEvent): ReplayFrameEvent | null => (isScrubTargetReplayEvent(event) ? safeScrub(event) : event);
